import React, { useState, useEffect } from "react";
import { PositionType } from "../types/position";
import { ValidationResult, BalanceCheckResult } from "../types/validation";
import ValidationStatusBar from "./ValidationStatusBar";
import BalanceChecker from "./BalanceChecker";
import TradeStatusIndicator from "./TradeStatusIndicator";
import { useTradeValidation } from "../hooks/useValidation";
import { useWalletDeposit, useCheckWalletBalance } from "../graphql/validation";
import { useTradeById } from "../graphql/trades";
import { useLogger } from "../services/logging";
import "./EnhancedPositionForm.css";

interface EnhancedPositionFormProps {
  tradeId: number;
  onSubmit: (data: PositionFormData) => void;
  onCancel: () => void;
  initialData?: Partial<PositionFormData>;
  isEdit?: boolean;
  className?: string;
}

interface PositionFormData {
  type: PositionType;
  quantity: number;
  price: number;
  date: string;
  notes?: string;
}

const EnhancedPositionForm: React.FC<EnhancedPositionFormProps> = ({
  tradeId,
  onSubmit,
  onCancel,
  initialData,
  isEdit = false,
  className = "",
}) => {
  const logger = useLogger("EnhancedPositionForm");
  const [formData, setFormData] = useState<PositionFormData>({
    type: PositionType.ENTRY,
    quantity: 0,
    price: 0,
    date: new Date().toISOString().split("T")[0],
    notes: "",
    ...initialData,
  });

  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [balanceResult, setBalanceResult] = useState<BalanceCheckResult | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Use the trade validation hook instead of direct fetch
  const { isTradeClosed, reopenTrade } = useTradeValidation(tradeId);

  // Use GraphQL hooks for wallet operations
  const {
    depositToWallet,
    loading: depositLoading,
    error: depositError,
  } = useWalletDeposit();
  const { checkWalletBalance, loading: balanceLoading } =
    useCheckWalletBalance();

  // Get trade information including walletId
  const { trade, loading: tradeLoading } = useTradeById(tradeId);

  // Combined loading state for all operations
  const isLoading =
    isSubmitting || depositLoading || balanceLoading || tradeLoading;

  const handleInputChange = (field: keyof PositionFormData, value: any) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    // Clear validation when user makes changes
    if (validation) {
      setValidation(null);
    }
  };

  const handleBalanceCheck = (result: BalanceCheckResult) => {
    setBalanceResult(result);
  };

  const handleDeposit = async (amount: number) => {
    try {
      // Get wallet ID from the trade data
      if (!trade?.walletId) {
        throw new Error("Trade wallet information not available");
      }

      // Use GraphQL mutation for wallet deposit
      await depositToWallet(
        amount,
        trade.walletId,
        `Deposit for trade ${tradeId}`,
      );

      // Refresh balance check using GraphQL query
      const newBalanceResult = await checkWalletBalance(
        tradeId,
        formData.quantity,
        formData.price,
      );

      if (newBalanceResult) {
        setBalanceResult(newBalanceResult);
        setValidation(null); // Clear any validation errors
      }
    } catch (error) {
      logger.error("Deposit failed", { method: "handleDeposit" }, error);
      // You might want to show an error message here
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestedActions: string[] = [];

    // Basic validation
    if (formData.quantity <= 0) {
      errors.push("Quantity must be greater than 0");
    }

    if (formData.price <= 0) {
      errors.push("Price must be greater than 0");
    }

    if (!formData.date) {
      errors.push("Date is required");
    }

    // Position type validation
    if (
      formData.type === PositionType.EXIT ||
      formData.type === PositionType.PARTIAL_EXIT
    ) {
      if (formData.quantity <= 0) {
        errors.push("Exit quantity must be greater than 0");
      }
    }

    // Balance validation
    if (balanceResult && !balanceResult.hasSufficientBalance) {
      errors.push("Insufficient wallet balance for this position");
      suggestedActions.push(
        `Deposit ${balanceResult.suggestedDeposit} to proceed`,
      );
    }

    // Trade status validation
    if (isTradeClosed) {
      errors.push("Cannot modify positions in closed trades");
      suggestedActions.push("Reopen the trade to make changes");
    }

    // Warnings
    if (formData.price > 1000000) {
      warnings.push("Price seems unusually high. Please verify the value.");
    }

    const newValidation: ValidationResult = {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestedActions,
    };

    setValidation(newValidation);
    return newValidation.isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit(formData);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to save position",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isTradeClosed &&
    formData.quantity > 0 &&
    formData.price > 0 &&
    (!validation || validation.isValid);

  return (
    <div className={`enhanced-position-form ${className}`}>
      <div className="form-header">
        <h3>{isEdit ? "Edit Position" : "Add New Position"}</h3>

        {/* Trade Status Indicator */}
        <TradeStatusIndicator
          tradeId={tradeId}
          isClosed={isTradeClosed}
          onReopen={reopenTrade}
        />
      </div>

      <form onSubmit={handleSubmit} className="position-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="type">Position Type</label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              disabled={isTradeClosed}
            >
              <option value={PositionType.ENTRY}>Entry</option>
              <option value={PositionType.EXIT}>Exit</option>
              <option value={PositionType.PARTIAL_EXIT}>Partial Exit</option>
              <option value={PositionType.ADD_TO_POSITION}>
                Add to Position
              </option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              value={formData.date}
              onChange={(e) => handleInputChange("date", e.target.value)}
              disabled={isTradeClosed}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="quantity">Quantity</label>
            <input
              type="number"
              id="quantity"
              value={formData.quantity}
              onChange={(e) =>
                handleInputChange("quantity", parseInt(e.target.value) || 0)
              }
              min="1"
              disabled={isTradeClosed}
            />
          </div>

          <div className="form-group">
            <label htmlFor="price">Price</label>
            <input
              type="number"
              id="price"
              value={formData.price}
              onChange={(e) =>
                handleInputChange("price", parseFloat(e.target.value) || 0)
              }
              min="0.01"
              step="0.01"
              disabled={isTradeClosed}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes (Optional)</label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            rows={3}
            disabled={isTradeClosed}
          />
        </div>

        {/* Balance Checker */}
        {formData.quantity > 0 && formData.price > 0 && !tradeLoading && (
          <BalanceChecker
            tradeId={tradeId}
            quantity={formData.quantity}
            price={formData.price}
            onBalanceCheck={handleBalanceCheck}
            onDeposit={handleDeposit}
          />
        )}

        {/* Validation Status Bar */}
        <ValidationStatusBar validation={validation} />

        {/* Submit Error */}
        {submitError && (
          <div className="submit-error">Error: {submitError}</div>
        )}

        {/* Deposit Error */}
        {depositError && (
          <div className="submit-error">
            Deposit Error: {depositError.message}
          </div>
        )}

        {/* Trade Loading Error */}
        {!trade && !tradeLoading && (
          <div className="submit-error">
            Error: Unable to load trade information
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="cancel-button"
            disabled={isLoading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={!canSubmit || isLoading}
          >
            {isLoading
              ? "Saving..."
              : isEdit
                ? "Update Position"
                : "Create Position"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EnhancedPositionForm;
